import {CAMPAIGN_KEYS} from "@/api/campaignApi";
import {PAYMENT_KEYS} from "@/api/paymentApi";
import Button from "@/components/Common/Button";
import {usePayment} from "@/hooks/usePayment";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import {getCampaignApplicationQueryKeys} from "@/store/campaignManagement";
import {paymentFailedModalDataAtom} from "@/store/jotaiAtoms";
import useUserStore from "@/store/user";
import {
  AddressElement,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import {type PaymentMethodCreateParams} from "@stripe/stripe-js";
import {useQueryClient} from "@tanstack/react-query";
import {useAtom} from "jotai";
import {useState, type FC} from "react";
import {useTranslation} from "react-i18next";

const CardForm: FC<{
  buttonLabel: string;
  invoiceIds: number[];
  isUpdate?: boolean;
  dettachingPaymentMethodId?: string;
  onSubmit?(): void;
  onCancel?(): void;
}> = ({buttonLabel, isUpdate = false, invoiceIds, onSubmit, onCancel, dettachingPaymentMethodId}) => {
  const {t} = useTranslation("common");
  const queryClient = useQueryClient();
  const user = useUserStore(state => state.user);
  const selectedCompanyId = useSelectedCompanyId();
  const [paymentFailedModalData, setPaymentFailedModalData] = useAtom(paymentFailedModalDataAtom);
  const stripe = useStripe();
  const stripeElements = useElements();
  const [address, setAddress] = useState<PaymentMethodCreateParams.BillingDetails | null>(null);
  const {detachPaymentMethod, detachLoading, attachPaymentMethod, attachLoading, payInvoices, invoicePayIsLoading} =
    usePayment();

  const createSource = async () => {
    const {paymentMethod} = await stripe?.createPaymentMethod({
      type: "card",
      card: stripeElements?.getElement(CardNumberElement)!,
      billing_details: {
        ...address,
        email: user.email!,
        phone: user.phonenumber!,
      },
    })!;

    if (paymentMethod?.id) {
      if (isUpdate) {
        await detachPaymentMethod({
          paymentMethodId: dettachingPaymentMethodId!,
        });
      }
      await attachPaymentMethod({paymentMethodId: paymentMethod.id});
      await payInvoices({invoiceIds});
      setTimeout(() => {
        const params = `company_profile_id=${selectedCompanyId}`;
        [PAYMENT_KEYS.qRegularInvoiceList(params), PAYMENT_KEYS.qRegularInvoiceListPdfExport(params)].map(queryKey =>
          queryClient.invalidateQueries({queryKey}),
        );
        const campaignIds = [
          ...new Set(
            paymentFailedModalData?.filter(invoice => invoice.campaign?.id).map(invoice => invoice.campaign!.id),
          ),
        ];
        campaignIds.forEach(campaignId => {
          [
            ...getCampaignApplicationQueryKeys(campaignId),
            CAMPAIGN_KEYS.qCampaign(campaignId),
            CAMPAIGN_KEYS.qCampaignManagement(campaignId),
            CAMPAIGN_KEYS.qCampaignPreparationApplications(campaignId),
          ].map(queryKey => queryClient.invalidateQueries({queryKey}));
        });
      }, 2500);
      setPaymentFailedModalData(null);
    }
  };

  return (
    <div className="border-neutral-grey-light-dark flex flex-col gap-5 rounded-xl border p-5">
      <Button isDisabled buttonStyle="White" className="h-auto w-full px-8 py-3 sm:w-fit sm:min-w-60">
        {buttonLabel}
      </Button>
      <div className="grid grid-cols-1 gap-y-5 md:grid-cols-2">
        <div className="col-span-2 md:col-span-1">
          <label htmlFor="cardNumber" className="text-neutral-black text-xl font-semibold">
            {t("cardNumber")}
          </label>
          <CardNumberElement
            id="cardNumber"
            options={{
              showIcon: true,
              style: {
                base: {
                  fontSize: "16px",
                  color: "#424770",
                  letterSpacing: "0.025em",
                  "::placeholder": {
                    color: "#aab7c4",
                    fontSize: "16px",
                  },
                },
                invalid: {
                  color: "#9e2146",
                },
              },
            }}
          />
        </div>
        <div className="flex items-center gap-7">
          <div>
            <label htmlFor="expiry" className="text-neutral-black text-xl font-semibold">
              {t("expirationDate")}
            </label>
            <CardExpiryElement
              id="expiry"
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    letterSpacing: "0.025em",
                    "::placeholder": {
                      color: "#aab7c4",
                      fontSize: "12px",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>
          <div>
            <label htmlFor="cvc" className="text-neutral-black text-xl font-semibold">
              CVC
            </label>
            <CardCvcElement
              id="cvc"
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#424770",
                    letterSpacing: "0.025em",
                    "::placeholder": {
                      color: "#aab7c4",
                      fontSize: "12px",
                    },
                  },
                  invalid: {
                    color: "#9e2146",
                  },
                },
              }}
            />
          </div>
        </div>
        <div className="col-span-2">
          <label htmlFor="billingAddress" className="text-neutral-black text-xl font-semibold">
            {t("billingAddress")}
          </label>
          <AddressElement
            options={{mode: "billing"}}
            onChange={e => setAddress(e.value as PaymentMethodCreateParams.BillingDetails)}
          />
        </div>
      </div>

      <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-5">
        <Button
          isDisabled={attachLoading || detachLoading || invoicePayIsLoading}
          buttonStyle="Blue"
          className="w-full px-8 py-4 sm:w-auto lg:min-w-60"
          onClick={async () => {
            await createSource();
            stripeElements?.getElement(AddressElement)?.clear();
            setTimeout(() => onSubmit?.(), 100);
          }}>
          {attachLoading || detachLoading || invoicePayIsLoading ? t("loading") : t("saveAndPay")}
        </Button>
        <Button
          isDisabled={attachLoading || detachLoading || invoicePayIsLoading}
          buttonStyle="White"
          className="w-full px-8 py-4 sm:w-auto lg:min-w-60"
          onClick={() => {
            stripeElements?.getElement(AddressElement)?.clear();
            setTimeout(() => onCancel?.(), 100);
          }}>
          {t("cancel")}
        </Button>
      </div>
    </div>
  );
};

export default CardForm;
